import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, computed, signal, inject } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'environments/environment';
import {
    Notificacion,
    NotificacionesResponse,
} from 'app/modules/pages/notificaciones/notificaciones.types';
import { tap, catchError, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NotificacionesService {
    // Signals
    public notificaciones = signal<Notificacion[]>([]);
    public leidas = signal<Set<number>>(new Set());

    // Computed
    public unreadCount = computed(() => {
        const total = this.notificaciones().length;
        const read = Array.from(this.leidas()).filter(id => 
            this.notificaciones().some(n => n.id === id)
        ).length;
        return total - read;
    });

    // Selected Notification (for details view)
    public selectedNotificacion = signal<Notificacion | null>(null);

    private readonly STORAGE_KEY = 'nuvalid_notificaciones_leidas';

    private cookieService = inject(CookieService);

    constructor(private _httpClient: HttpClient) {
        this._loadLeidasFromStorage();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Fetch notificaciones from backend
     */
    fetchNotificaciones() {
        const url = `${environment.urlNuvalidLite}/notificaciones/estado/ACTIVE`;
        const token = this.cookieService.get('st-access-token');
        const headers = new HttpHeaders({
            'Authorization': `Bearer ${token}`
        });

        return this._httpClient.get<NotificacionesResponse>(url, { headers }).pipe(
            tap((response) => {
                if (response && response.response && response.response.notificaciones) {
                    this.notificaciones.set(response.response.notificaciones);
                }
            }),
            catchError((error) => {
                console.error('Error fetching notificaciones', error);
                return of(null);
            })
        );
    }

    /**
     * Mark a single notification as read
     */
    marcarComoLeida(id: number): void {
        const currentLeidas = new Set(this.leidas());
        if (!currentLeidas.has(id)) {
            currentLeidas.add(id);
            this.leidas.set(currentLeidas);
            this._saveLeidasToStorage();
        }
    }

    /**
     * Mark all current notifications as read
     */
    marcarTodasComoLeidas(): void {
        const currentLeidas = new Set(this.leidas());
        let changed = false;
        
        this.notificaciones().forEach(n => {
            if (!currentLeidas.has(n.id)) {
                currentLeidas.add(n.id);
                changed = true;
            }
        });

        if (changed) {
            this.leidas.set(currentLeidas);
            this._saveLeidasToStorage();
        }
    }

    /**
     * Set selected notification by ID
     */
    seleccionarNotificacion(id: number): void {
        const notificacion = this.notificaciones().find(n => n.id === id);
        if (notificacion) {
            this.selectedNotificacion.set(notificacion);
            this.marcarComoLeida(id);
        } else {
            this.selectedNotificacion.set(null);
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private _loadLeidasFromStorage(): void {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed)) {
                    this.leidas.set(new Set(parsed));
                }
            }
        } catch (e) {
            console.error('Error loading read notifications from local storage', e);
        }
    }

    private _saveLeidasToStorage(): void {
        try {
            const arr = Array.from(this.leidas());
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(arr));
        } catch (e) {
            console.error('Error saving read notifications to local storage', e);
        }
    }
}
